import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTheme } from '@insight-health-ai/roseate/theme';

const rippleAnimation = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
`;

const sizeMap = {
  sm: '6px',
  md: '9px',
  lg: '10px',
};

const appearanceMap = {
  primary: '#10b981', // green-500
  danger: '#ef4444', // red-500
  secondary: '#6b7280', // gray-500
  alternate: '#5667E9',
};

const RippleContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CoreDot = styled.div<{
  size: keyof typeof sizeMap;
  color: string;
}>`
  width: ${(props) => sizeMap[props.size]};
  height: ${(props) => sizeMap[props.size]};
  background-color: ${({ color }) => color};
  border-radius: 50%;
  position: relative;
  z-index: 2;
`;

const RippleRing = styled.div<{
  size: keyof typeof sizeMap;
  color: string;
  delay: number;
}>`
  position: absolute;
  width: ${(props) => sizeMap[props.size]};
  height: ${(props) => sizeMap[props.size]};
  border-radius: 50%;
  border: 2px solid ${({ color }) => color};
  animation: ${rippleAnimation} 2s ease-out infinite;
  animation-delay: ${({ delay }) => delay}s;
  pointer-events: none;
`;

export type RippleDotProps = {
  size?: keyof typeof sizeMap;
  appearance?: keyof typeof appearanceMap;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export const RippleDot = ({
  size = 'md',
  appearance = 'primary',
  className = '',
  ...props
}: RippleDotProps) => {
  const { theme } = useTheme();

  const color = useMemo(() => {
    if (appearance === 'primary') {
      return theme.colors.primary.DEFAULT;
    }
    if (appearance === 'danger') {
      return theme.colors.danger.DEFAULT;
    }
    if (appearance === 'secondary') {
      return theme.colors.secondary.DEFAULT;
    }
    if (appearance === 'alternate') {
      return appearanceMap.alternate;
    }
    return theme.colors.primary.DEFAULT;
  }, [appearance, theme]);

  return (
    <div className={`relative inline-block ${className}`} {...props}>
      <RippleContainer>
        <CoreDot size={size} color={color} />
        <RippleRing size={size} color={color} delay={1} />
        <RippleRing size={size} color={color} delay={2} />
        <RippleRing size={size} color={color} delay={3} />
      </RippleContainer>
    </div>
  );
};
